// MentionPicker — floating selector inside a comment textarea.
//
// Behaviour:
// 1. Parent watches its own textarea for `@<query>` token at the caret.
// 2. When token exists, parent renders <MentionPicker> with the query +
//    callback. Picker fetches participants and shows filtered list grouped
//    by tenant.
// 3. Picker accepts keyboard navigation (↑↓ Enter) via `onPickerKeyDown`,
//    which the parent forwards from the textarea keydown handler.
// 4. On selection the parent receives `{id, name}` and replaces the partial
//    @token with `@<name> ` and appends `id` to the mentions array.
//
import React, { useEffect, useImperativeHandle, useMemo, useState, forwardRef } from "react";
import { useIncidentParticipants } from "../api/sitroom";

const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase() || "??";

const roleColor = (role) => {
  if (role === "platform_admin") return "#f3b14a";
  if (role === "tenant_admin") return "#9ec3ec";
  if (role === "escalation_manager") return "#c89adb";
  if (role === "responder") return "#4ed084";
  return "#9aa1b3";
};

export const MentionPicker = forwardRef(function MentionPicker(
  { incidentId, query, visibility, anchor, onSelect },
  ref,
) {
  const { data: grouped = {} } = useIncidentParticipants(
    incidentId,
    visibility === "internal" ? "internal" : "all_parties",
    Boolean(incidentId),
  );
  const [activeIndex, setActiveIndex] = useState(0);

  const flat = useMemo(() => {
    const q = (query || "").toLowerCase();
    const out = [];
    Object.entries(grouped).forEach(([tenant, users]) => {
      const matches = users.filter(
        (u) =>
          !q ||
          (u.name || "").toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q),
      );
      if (matches.length) out.push({ tenant, users: matches });
    });
    return out;
  }, [grouped, query]);

  const flatUsers = useMemo(() => flat.flatMap((g) => g.users), [flat]);

  // Reset active index when filter changes
  useEffect(() => {
    setActiveIndex(0);
  }, [query, incidentId, visibility]);

  // Expose key handler to the parent textarea
  useImperativeHandle(
    ref,
    () => ({
      handleKey: (e) => {
        if (flatUsers.length === 0) return false;
        if (e.key === "ArrowDown") {
          e.preventDefault();
          setActiveIndex((i) => Math.min(i + 1, flatUsers.length - 1));
          return true;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setActiveIndex((i) => Math.max(i - 1, 0));
          return true;
        }
        if (e.key === "Enter" || e.key === "Tab") {
          e.preventDefault();
          onSelect(flatUsers[activeIndex]);
          return true;
        }
        if (e.key === "Escape") {
          e.preventDefault();
          onSelect(null);
          return true;
        }
        return false;
      },
    }),
    [flatUsers, activeIndex, onSelect],
  );

  if (flatUsers.length === 0) {
    return (
      <div
        className="absolute z-50 rounded-md"
        style={{
          left: anchor?.left || 0,
          bottom: anchor?.bottom || 40,
          background: "#0d0f18",
          border: "1px solid rgba(255,255,255,0.12)",
          padding: "10px 12px",
          fontFamily: "DM Mono, monospace",
          fontSize: 10,
          color: "#9198b0",
          letterSpacing: "0.5px",
          boxShadow: "0 12px 32px rgba(0,0,0,0.5)",
        }}
        data-testid="mention-picker-empty"
      >
        No matches for &ldquo;{query}&rdquo;
      </div>
    );
  }

  // Highlight the currently-active row in the flat list
  let runningIdx = -1;

  return (
    <div
      className="absolute z-50 rounded-md"
      style={{
        left: anchor?.left || 0,
        bottom: anchor?.bottom || 40,
        width: 260,
        maxHeight: 240,
        overflowY: "auto",
        background: "#0d0f18",
        border: "1px solid rgba(255,255,255,0.12)",
        boxShadow: "0 12px 32px rgba(0,0,0,0.55)",
      }}
      data-testid="mention-picker"
    >
      {flat.map((group) => (
        <div key={group.tenant}>
          <div
            className="px-3 py-1.5"
            style={{
              fontFamily: "DM Mono, monospace",
              fontSize: 9,
              letterSpacing: "1.4px",
              color: "#9198b0",
              textTransform: "uppercase",
              background: "rgba(255,255,255,0.02)",
            }}
          >
            {group.tenant}
          </div>
          {group.users.map((u) => {
            runningIdx += 1;
            const active = runningIdx === activeIndex;
            const c = roleColor(u.role);
            return (
              <button
                key={u.id}
                onClick={() => onSelect(u)}
                onMouseEnter={() => setActiveIndex(runningIdx)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left"
                style={{
                  background: active ? "rgba(24,95,165,0.18)" : "transparent",
                  borderLeft: active ? "2px solid #185FA5" : "2px solid transparent",
                }}
                data-testid={`mention-option-${u.id}`}
              >
                <span
                  className="inline-flex items-center justify-center rounded-full flex-shrink-0"
                  style={{
                    width: 22,
                    height: 22,
                    background: "rgba(24,95,165,0.40)",
                    border: "1px solid rgba(24,95,165,0.55)",
                    color: "#cfe1f6",
                    fontFamily: "DM Mono, monospace",
                    fontSize: 9,
                  }}
                >
                  {initials(u.name || u.email)}
                </span>
                <span
                  className="flex-1 truncate"
                  style={{
                    fontFamily: "Syne, sans-serif",
                    fontSize: 12,
                    color: "#f0f2f8",
                    fontWeight: 500,
                  }}
                >
                  {u.name || u.email}
                </span>
                <span
                  style={{
                    fontFamily: "DM Mono, monospace",
                    fontSize: 8,
                    color: c,
                    letterSpacing: "0.8px",
                    textTransform: "uppercase",
                  }}
                >
                  {(u.role || "").replace(/_/g, " ")}
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
});

// Helper: extract the current @-token (if any) from a textarea given caret pos.
// Returns { token, start, end } when a valid @-token sits before the caret.
export const detectMentionToken = (text, caret) => {
  if (caret <= 0) return null;
  // Walk back from caret to find an `@` or whitespace boundary.
  let i = caret - 1;
  while (i >= 0) {
    const ch = text[i];
    if (ch === "@") {
      // Must be at start OR preceded by whitespace
      if (i === 0 || /\s/.test(text[i - 1])) {
        const token = text.slice(i + 1, caret);
        // Only valid while the token has no whitespace yet
        if (/\s/.test(token)) return null;
        return { token, start: i, end: caret };
      }
      return null;
    }
    if (/\s/.test(ch)) return null;
    i -= 1;
  }
  return null;
};
